import {
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { BuySetting, Game, NeutronState, Producer } from "../../types/game";
import { useSpring, animated } from "react-spring";
import {
  generateProducts,
  getCost,
  getCostsTillNextTimes10Upgrade,
  getTimes10Progress,
  isMoleculeUnlocked,
} from "../../lib/calculator";
import { prettifyNumber } from "../../lib/prettifier";
import { BuyProducerButton } from "../GameButtons";
import {
  getAchievementById,
  unlockAchievement,
} from "../../types/achievements";
import { SnackbarContext } from "../../provider/SnackbarProvider";
import { GameContext } from "../../provider/GameProvider";
import { Atom } from "@phosphor-icons/react/dist/ssr";
import { get, set } from "lodash";
import {
  loadObjectFromLocalStorage,
  saveObjectToLocalStorage,
} from "../../save/localSave";
const standardWidth = "400px";
const maxWidth = "400px";

export default function ProducerCard(props: { index: number }) {
  const {
    achievementMultiplier,
    buySetting,
    timeProgressArray,
    setTimeProgressArray,
    game,
    setGame,
  } = useContext(GameContext);
  const producer = game.producers[props.index];
  const nameToLong = producer.name.length > 10;
  const productNameToLong = producer.productName.length > 10;
  const { showSnackbar } = useContext(SnackbarContext);

  const electronUnlocked = useMemo(() => {
    return isMoleculeUnlocked("Propane", game);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.molecule.graphNodes]);
  const protonUnlocked = useMemo(() => {
    return isMoleculeUnlocked("Butane", game);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.molecule.graphNodes]);
  const [borderColor, setBorderColor] = useState("lightblue");

  const updateBorderColor = useMemo(() => {
    if (
      game.producers[0].value.eq(0) &&
      game.producers[1].produceTime <= timeProgressArray[1] &&
      !loadObjectFromLocalStorage("hideUpdateBorderColor") &&
      props.index === 1
    ) {
      saveObjectToLocalStorage("showHoldCTipp", true);
      return true;
    }
    return false;
  }, [game.producers, props.index, timeProgressArray]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (updateBorderColor) {
        if (borderColor === "lightblue") {
          setBorderColor("yellow");
        } else {
          setBorderColor("lightblue");
        }
      } else {
        setBorderColor("lightblue");
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [borderColor, updateBorderColor]);
  return (
    <Card
      style={{
        backgroundColor: "transparent",
        width: standardWidth,
        maxWidth: maxWidth,
        borderColor: borderColor,
        borderWidth: "2px",
        borderStyle: "solid",
        userSelect: "none",
        borderRadius: 7,
        zIndex: 10,
      }}
      onClick={() => {
        if (
          timeProgressArray[props.index] >=
          producer.produceTime * game.neutronState.timeMultiplier
        ) {
          setGame((prev: Game) => ({
            ...prev,
            producers: prev.producers.map((p: Producer, index: number) => {
              if (index === props.index - 1) {
                return {
                  ...p,
                  value: p.value.plus(
                    generateProducts(producer, achievementMultiplier, game)
                  ),
                };
              }
              return p;
            }),
          }));
          setTimeProgressArray[props.index](() => 0);
          saveObjectToLocalStorage("hideUpdateBorderColor", true);
        }
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Typography
          variant="h5"
          style={{
            color: "white",
            fontWeight: "bold",
            paddingInline: 8,
            borderColor: "lightblue",
            borderWidth: "2px",
            borderStyle: "solid",
            borderRadius: "7px 0 7px 0px",
            display: "flex",
            borderLeft: "none",
            borderTop: "none",
            fontSize: nameToLong ? 14 : 22,
            textAlign: "center",
            alignItems: "center",
          }}
          onClick={() => {
            console.log("triggered");
          }}
        >
          <span
            style={{
              paddingTop: nameToLong ? 6 : 0,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {producer.name}
          </span>
          {props.index === 1 && (electronUnlocked || protonUnlocked) && (
            <Tooltip
              title={
                <div style={{ display: "block" }}>
                  {electronUnlocked && (
                    <p>Electrons make all Producers 50% faster.</p>
                  )}
                  {protonUnlocked && (
                    <p>
                      Protons make alle Producers 10 times slower but 15 times
                      more efficient.
                    </p>
                  )}
                </div>
              }
            >
              <IconButton
                style={{ paddingRight: 0 }}
                onClick={() => {
                  setGame((prev) => {
                    const newState = getNextNeutronState(
                      prev.neutronState.state,
                      electronUnlocked,
                      protonUnlocked
                    );
                    const newNeutronState = getNeutronState(newState);
                    return {
                      ...prev,
                      neutronState: newNeutronState,
                      producers: prev.producers.map((p, index) => {
                        if (index === 1) return { ...p, name: newState };
                        return p;
                      }),
                    };
                  });
                  setTimeProgressArray.forEach((setProgressTime) => {
                    setProgressTime(0);
                  });
                }}
              >
                <Atom style={{ color: "white", paddingRight: 0 }} size={20} />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
        <div
          style={{
            color: "white",
            padding: 8,
            fontSize: 12,
            paddingLeft: nameToLong ? 8 : 20,
          }}
        >
          {prettifyNumber(producer.value)} units
        </div>
      </div>
      <CardContent
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          width: "100%",
          padding: 8,
        }}
      >
        <div style={{ display: "flex" }}>
          <span style={{ color: "#2196f3", paddingRight: 8 }}>
            {prettifyNumber(
              generateProducts(producer, achievementMultiplier, game)
            )}{" "}
            {`${
              productNameToLong
                ? producer.productShortName
                : producer.productName
            }`}
          </span>
          <span style={{ color: "white", paddingRight: 8 }}>in</span>
          <span style={{ color: "white" }}>
            {(
              (producer.produceTime * game.neutronState.timeMultiplier < 100
                ? 100
                : producer.produceTime * game.neutronState.timeMultiplier) /
              1000
            ).toFixed(2)}
            s
          </span>
        </div>
        <div style={{ marginTop: -30, zIndex: 100 }}>
          <BuyProducerButton
            producer={producer}
            onClick={() => {
              if (buySetting === BuySetting.BuyUntilTen) {
                const cost = getCostsTillNextTimes10Upgrade(producer);
                if (game.producers[0].value.gte(cost)) {
                  const times10progress = getTimes10Progress(
                    producer.upgradeLevel
                  );
                  const till10 = 10 - times10progress;
                  setGame((prev) => ({
                    ...prev,
                    producers: prev.producers.map((p, index) => {
                      if (index === 0) {
                        return {
                          ...p,
                          value: p.value.minus(cost),
                        };
                      }
                      if (index === props.index) {
                        return {
                          ...p,
                          value: p.value.plus(till10),
                          upgradeLevel: p.upgradeLevel + till10,
                        };
                      }
                      return p;
                    }),
                  }));
                }
              } else if (buySetting === BuySetting.BuyOne) {
                const cost = getCost(producer);
                if (game.producers[0].value.gte(cost)) {
                  setGame((prev) => ({
                    ...prev,
                    producers: prev.producers.map((p, index) => {
                      if (index === 0) {
                        return {
                          ...p,
                          value: p.value.minus(cost),
                        };
                      }
                      if (index === props.index) {
                        return {
                          ...p,
                          value: p.value.plus(1),
                          upgradeLevel: p.upgradeLevel + 1,
                        };
                      }
                      return p;
                    }),
                  }));
                  const unlocked = unlockAchievement(
                    producer.layerLevel,
                    game.unlockedAchievementIds,
                    setGame
                  );
                  if (unlocked) {
                    showSnackbar(
                      `Achievement unlocked: ${
                        getAchievementById(producer.layerLevel)?.name
                      }`
                    );
                  }
                }
              }
            }}
          />
          <div
            style={{
              color: "white",
              fontSize: 10,
              display: "flex",
              justifyContent: "flex-end",
              paddingRight: 4,
              alignItems: "center",
              paddingTop: 4,
            }}
          >
            purchased {producer.upgradeLevel} times
          </div>
        </div>
      </CardContent>
      <ProgressBar producer={producer} time={timeProgressArray[props.index]} />
    </Card>
  );
}

function ProgressBar(props: { producer: Producer; time: number }) {
  const { game } = useContext(GameContext);
  const produceTime =
    props.producer.produceTime * game.neutronState.timeMultiplier;
  const widthSpring = useSpring({
    width: `${(props.time / produceTime) * 100}%`,
    config: { duration: 50, easing: (t) => t },
  });
  const remainingSeconds = (
    produceTime - props.time < 0 ? 0 : (produceTime - props.time) / 1000
  ).toFixed(1);

  const progressBarText =
    remainingSeconds === "0.0" || remainingSeconds === "-0.0"
      ? "Collect"
      : `${remainingSeconds}s`;
  const isMaxSpeed = produceTime <= 100;
  return (
    <Card
      style={{
        background: "transparent",
        borderColor: "lightblue",
        borderWidth: "2px",
        borderStyle: "solid",
        width: "100%",
        minWidth: "300px",
        borderRadius: 0,
        borderLeft: "none",
        borderRight: "none",
        marginBottom: -7,
      }}
    >
      <Card
        style={{
          background: "transparent",
          width: "100%",
          //maxWidth: "400px",
          height: "30px",
          padding: 0,
          borderRadius: 0,
        }}
      >
        <animated.div
          style={{
            top: 0,
            left: 0,
            background: "green",
            width: isMaxSpeed ? "100%" : widthSpring.width,
          }}
        >
          <div
            style={{
              color: "white",
              marginLeft: isMaxSpeed ? "150px" : "170px",
            }}
          >
            {isMaxSpeed ? "Max Speed" : progressBarText}
          </div>
        </animated.div>
      </Card>
    </Card>
  );
}

function getNextNeutronState(
  currentState: "Neutron" | "Electron" | "Proton",
  electronUnlocked: boolean,
  protonUnlocked: boolean
): "Neutron" | "Electron" | "Proton" {
  console.log("currentState", currentState);
  console.log("electronUnlocked", electronUnlocked);
  console.log("protonUnlocked", protonUnlocked);
  if (currentState === "Neutron") {
    if (electronUnlocked) {
      return "Electron";
    } else if (protonUnlocked) {
      return "Proton";
    }
  }
  if (currentState === "Electron") {
    if (protonUnlocked) {
      return "Proton";
    } else {
      return "Neutron";
    }
  }
  if (currentState === "Proton") {
    return "Neutron";
  }
  return currentState;
}

function getNeutronState(
  state: "Neutron" | "Electron" | "Proton"
): NeutronState {
  if (state === "Neutron") {
    return {
      state: "Neutron",
      timeMultiplier: 1,
      multiplierMultiplier: 1,
    };
  }
  if (state === "Electron") {
    return {
      state: "Electron",
      timeMultiplier: 0.5,
      multiplierMultiplier: 1,
    };
  }
  if (state === "Proton") {
    return {
      state: "Proton",
      timeMultiplier: 10,
      multiplierMultiplier: 15,
    };
  }
  return {
    state: "Neutron",
    timeMultiplier: 1,
    multiplierMultiplier: 1,
  };
}
